/**
 * Release 3 D — "Who can see this board" inside the Edit board dialog (S2). Workspace (default) or private: a private
 * board hides its whole subtree from everyone except its owner, ADMINs and people invited to a board inside it. Making
 * a board private makes the caller its owner; an ADMIN may hand a private board to another member.
 */
import { useState } from 'react';
import { Lock, Users } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { useMembers, useSetBoardVisibility } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { usePermission } from '@/features/workspace/usePermission';
import { Label, Select } from '@/components/Input';
import { cx } from '@/lib/format';
import { boardOwnerId, boardVisibility, type BoardVisibility } from './visibility';

const OPTIONS: { value: BoardVisibility; label: string; hint: string; icon: typeof Lock }[] = [
  { value: 'workspace', label: 'Workspace', hint: 'Everyone with access to the workspace, as their role allows.', icon: Users },
  { value: 'private', label: 'Private', hint: 'Only you, workspace admins and people invited to this board (or a board inside it).', icon: Lock },
];

export function BoardVisibilityControl({ board }: { board: Board }) {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const me = useSessionStore((s) => (s.principal.kind === 'member' ? s.principal.memberId : null));
  const isAdmin = usePermission('ADMIN');
  const visibility = boardVisibility(board);
  const ownerId = boardOwnerId(board);
  const canChange = visibility === 'workspace' || isAdmin || ownerId === me;
  const members = useMembers(isAdmin && visibility === 'private' ? wsId : undefined);
  const set = useSetBoardVisibility(board.id);
  const [pending, setPending] = useState<BoardVisibility | null>(null);
  const owner = members.data?.find((m) => m.id === ownerId);

  const change = (next: BoardVisibility, owner_member_id?: string) => {
    setPending(next);
    set.mutate(
      { visibility: next, ...(owner_member_id ? { owner_member_id } : {}) },
      {
        onSettled: () => setPending(null),
        onSuccess: (b) => toast({ kind: 'success', title: boardVisibility(b) === 'private' ? `“${b.name}” is now private` : `“${b.name}” is visible to the workspace` }),
        onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Could not change visibility' }),
      },
    );
  };

  return (
    <div data-board-visibility>
      <Label>Who can see this board</Label>
      <div role="radiogroup" aria-label="Board visibility" className="grid gap-1.5 sm:grid-cols-2">
        {OPTIONS.map((o) => {
          const Icon = o.icon;
          const active = (pending ?? visibility) === o.value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={!canChange || set.isPending}
              onClick={() => !active && change(o.value)}
              className={cx('flex items-start gap-2 rounded-md border p-2 text-left text-sm', active ? 'border-accent bg-accent-soft' : 'border-line hover:bg-bg-sunken', (!canChange || set.isPending) && 'cursor-not-allowed opacity-70')}
              data-visibility-option={o.value}
            >
              <Icon size={14} className="mt-0.5 shrink-0" />
              <span>
                <span className="font-medium">{o.label}</span>
                <span className="block text-xs text-fg-subtle">{o.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
      {visibility === 'private' && (
        <p className="mt-1.5 text-xs text-fg-subtle" data-board-owner>
          {ownerId === me ? 'You own this private board.' : owner ? `Owned by ${owner.user.display_name}.` : 'Owned by another member.'}
          {!canChange && ' Only the owner or an admin can change its visibility.'}
        </p>
      )}
      {visibility === 'private' && isAdmin && members.data && (
        <div className="mt-1.5">
          <Label htmlFor="board-owner">Owner</Label>
          <Select id="board-owner" value={ownerId ?? ''} disabled={set.isPending} onChange={(e) => change('private', e.target.value)} className="h-8 text-sm">
            {members.data.filter((m) => m.status === 'active').map((m) => (
              <option key={m.id} value={m.id}>
                {m.user.display_name}
              </option>
            ))}
          </Select>
        </div>
      )}
    </div>
  );
}
